import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/context/I18n";

/**
 * Lets any listener pull an artist the catalogue is missing, instead of having
 * to ask an admin. The server does the same discovery as the admin import but
 * with a smaller budget and a per-user cooldown.
 */
export default function RequestArtist({ onDone }) {
  const { t } = useI18n();
  const [artist, setArtist] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const name = artist.trim();
    if (!name || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/music/request-artist", { artist: name });
      // The request no longer imports on the spot; it puts the artist at the
      // front of the queue and their records arrive over the next while.
      if (data.queued) {
        toast.success(`${data.artist}: ${t("request_queued")}`);
      } else {
        toast.message(`${data.artist}: ${t("request_already")}`);
      }
      setArtist("");
      if (onDone) onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="request-artist" className="flex items-center gap-2 w-full">
      <div className="relative flex-1 min-w-0">
        <Sparkles className="w-4 h-4 text-[#FF2A4D] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          data-testid="request-artist-input"
          value={artist}
          onChange={(e) => setArtist(e.target.value)}
          placeholder={t("request_placeholder")}
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-full pl-9 pr-4 py-2.5 text-sm placeholder-zinc-600 focus:outline-none focus:border-[#FF2A4D] focus:ring-1 focus:ring-[#FF2A4D]"
        />
      </div>
      <button
        type="submit"
        data-testid="request-artist-btn"
        disabled={loading || !artist.trim()}
        className="bg-[#FF2A4D] hover:bg-[#E61C3D] disabled:opacity-50 rounded-full px-5 py-2.5 text-sm font-semibold shrink-0 flex items-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {loading ? t("request_working") : t("request_btn")}
      </button>
    </form>
  );
}
